import { z } from 'zod';
import * as dt from '../data.types.js';
import {
  BaseResponseSchema,
  containerSlotSchema,
  containerTypeSchema,
  dateSchema,
  integerIdSchema,
  ipAddressSchema,
  uuidSchema,
  waferAuditStatusSchema,
  waferFingerprintSchema,
} from './primitives.js';
import { FileMetadataSchema } from './files.schemas.js';
import { gatewaySerialNumberParamSchema } from './params.js';
import {
  containerSlotScanJobOptionsSchema,
  containerSlotScanJobPhaseSchema,
  containerSlotScanSlotPhaseSchema,
  ocrPhaseSchema,
} from '../container-slot-scan.schemas.js';

import { OCR_RESOLUTION_METHOD } from '../data.types.js';
// IoT Status Schema - Must be defined before EFEM schemas
export const efemIotCommandSchema = z.nativeEnum(dt.EFEM_IOT_COMMAND);


// EFEM Integration Schemas
export const EfemClientDataSchema = z.object({
  gatewaySerialNumber: gatewaySerialNumberParamSchema.nullable(),
  name: z.string(),
  isActive: z.boolean(),
  lastSeen: z.string().optional(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export const CreateEfemClientSchema = z.object({
  name: z.string(),
  serialNumber: z.string().optional(),
});

// ===== AWS IoT Core Schemas =====

/**
 * Device provisioning request
 * Creates AWS IoT Thing + Certificate for EFEM client
 */
export const ProvisionDeviceRequestSchema = z.object({
  tenantId: uuidSchema,
  clientId: integerIdSchema,
  deviceName: z.string().min(1).max(255),
});



/**
 * Device provisioning response
 * Contains certificate credentials for EFEM client (ONE-TIME DOWNLOAD)
 */
export const ProvisionDeviceResponseSchema = z.object({
  thingName: z.string(),
  certificateId: z.string(),
  certificateArn: z.string(),
  certificatePem: z.string(), // Public certificate (X.509 PEM format)
  privateKey: z.string(),     // Private key (NEVER store in backend)
  publicKey: z.string(),      // Public key (optional)
  iotEndpoint: z.string(),    // AWS IoT endpoint URL
  region: z.string(),         // AWS region
});



/**
 * IoT command request
 * Sent from backend to EFEM client via MQTT
 */
export const IoTCommandRequestSchema = z.object({
  tenantId: uuidSchema,
  clientId: z.string(), // IoT Thing name (e.g., "efem-{tenantId}-1")
  command: efemIotCommandSchema,
  payload: z.record(z.any()).optional(), // Command-specific data
  orchestratorType: z.enum(['efem', 'location']).default('efem'),
});



/**
 * IoT command response
 * Sent from EFEM client to backend via MQTT (response topic)
 */
/** IoT Rule → iot-rpc-bridge Lambda ingress (loose; unknown edge fields preserved). */
export const IoTRuleControlResponseLooseSchema = z
  .object({
    requestId: z.string().min(1),
  })
  .passthrough();



/** Strict envelope after BLPOP on wafer backend (EFEM uses IoTCommandResponseSchema). */
export const IotRpcResponseEnvelopeSchema = z
  .object({
    requestId: z.string().min(1),
    success: z.boolean(),
    data: z.unknown().optional(),
    error: z
      .object({
        code: z.string(),
        message: z.string(),
      })
      .optional(),
    timestamp: z.string().optional(),
    orchestratorId: z.string().optional(),
    topic: z.string().optional(),
  })
  .passthrough();



export const IoTCommandResponseSchema = z.object({
  requestId: uuidSchema,
  success: z.boolean(),
  data: z.any().optional(),   // Command response data
  error: z.object({
    code: z.string(),
    message: z.string(),
  }).optional(), // Structured error if failed
  timestamp: dateSchema.optional(),
  orchestratorId: z.string().optional(),
});



// ===== END AWS IoT Core Schemas =====

export const EfemConfigSchema = z.object({
  tenantId: uuidSchema,
  maxClients: z.number().int().min(0),
  clients: z.array(EfemClientDataSchema)
});

export const EfemClientStatusSchema = BaseResponseSchema.extend({
  data: z.object({
    gatewaySerialNumber: gatewaySerialNumberParamSchema,
    name: z.string(),
    isOnline: z.boolean(),
    responseTime: z.number().nullable().optional(),
    error: z.string().optional(),
    lastSeen: dateSchema.optional()
  })
});


export const GetEfemClientsResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    clients: z.array(z.object({
      gatewaySerialNumber: gatewaySerialNumberParamSchema.nullable(),
      name: z.string(),
      isActive: z.boolean(),
      lastSeen: dateSchema.optional(),
      tenantId: uuidSchema,
      createdAt: dateSchema,
      updatedAt: dateSchema,
      status: z.nativeEnum(dt.ONLINE_OFFLINE_STATUS),
    }))
  })
});



export const EfemStatusResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    config: EfemConfigSchema.nullable(),
    clientStatuses: z.array(EfemClientStatusSchema)
  })
});

export const EfemServiceStatusSchema = z.object({
  isOnline: z.boolean(),
  isInitialized: z.boolean(),
  version: z.string(),
  lastHeartbeat: dateSchema,
  ipAddress: ipAddressSchema
})

export const EfemClientAuthenticationResultSchema = z.object({
  efemClientId: integerIdSchema,
  isAuthenticated: z.boolean(),
  error: z.string().optional(),
  serviceStatus: EfemServiceStatusSchema.optional()
})

export const EfemClientAuthenticationResultResponseSchema = BaseResponseSchema.extend(({
  data: z.array(EfemClientAuthenticationResultSchema)
}))



export const LoadPortSchema = z.object({
  id: integerIdSchema,
  name: z.string(),
  status: z.nativeEnum(dt.LOAD_PORT_STATUS),
  size: z.nativeEnum(dt.EFEM_LOAD_PORT_SIZE),
  containerType: containerTypeSchema.optional(),
  // Hardware carrier ID string (RFID tag / barcode) returned by the EFEM adapter.
  // NOT a database integer ID — use ContainerMapper to associate a DB container.
  containerId: z.string().optional(),
});

export const LoadPortsResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    loadPorts: z.array(LoadPortSchema)
  })
});

export const SlotInfoSchema = z.object({
  slotNumber: z.number().int().min(0),
  isOccupied: z.boolean(),
  waferId: waferFingerprintSchema.optional()
});

export const SlotMapResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    loadPortId: z.number().int().positive(),
    containerId: z.string().optional(), // Container name/identifier, not numeric DB ID
    containerType: containerTypeSchema,
    slots: z.array(SlotInfoSchema)
  })
});

/**
 * Batch Scan Status Schemas - Must be defined before usage
 */
export const batchScanStatusSchema = z.nativeEnum(dt.BATCH_SCAN_STATUS);
export const slotScanStatusSchema = z.nativeEnum(dt.SLOT_SCAN_STATUS);
export const ocrErrorTypeSchema = z.nativeEnum(dt.OCR_ERROR_TYPE);
export const waferHistoryEventSchema = z.nativeEnum(dt.WAFER_HISTORY_EVENT);
export const healthStatusSchema = z.nativeEnum(dt.HEALTH_STATUS);
export const healthCheckStatusSchema = z.nativeEnum(dt.HEALTH_CHECK_STATUS);
export const readinessStatusSchema = z.nativeEnum(dt.READINESS_STATUS);
export const livenessStatusSchema = z.nativeEnum(dt.LIVENESS_STATUS);
export const mlComponentStatusSchema = z.nativeEnum(dt.ML_COMPONENT_STATUS);
export const imageQualitySchema = z.nativeEnum(dt.IMAGE_QUALITY);
export const onlineOfflineStatusSchema = z.nativeEnum(dt.ONLINE_OFFLINE_STATUS);

export const WaferFingerprintCollectionRequestSchema = z.object({
  loadPortId: z.number().int().positive(),
  slotNumbers: z.array(containerSlotSchema)
});


export const WaferFingerprintResultSchema = z.object({
  loadPortId: z.number().int().positive(),
  slotNumber: z.number().int().positive(),
  fingerprintImage: FileMetadataSchema,
  fingerprintString: waferFingerprintSchema.optional(),
  timestamp: z.string(),
  status: slotScanStatusSchema,
  errorMessage: z.string().optional()
});

export const WaferImageCollectionResponseSchema = BaseResponseSchema.extend({
  data: z.array(WaferFingerprintResultSchema)
});

/**
 * Device status from Thing Shadow
 * NOTE: Placed after LoadPortSchema to avoid forward reference
 */
export const DeviceShadowStatusSchema = z.object({
  connected: z.boolean(),
  lastHeartbeat: dateSchema.optional(),
  firmwareVersion: z.string().optional(),
  capabilities: z.array(z.string()).optional(),
  loadPorts: z.array(LoadPortSchema).optional(),
});





export const WaferAuditResultSchema = z.object({
  loadPortId: z.number().int().positive().optional(),
  slotNumber: containerSlotSchema,
  expectedWaferFingerprint: waferFingerprintSchema.optional(),
  actualWaferFingerprint: waferFingerprintSchema.optional(),
  expectedWaferId: integerIdSchema.optional(),
  expectedLayerName: z.string().max(255).trim().optional(),
  expectedCompanyName: z.string().max(255).trim().optional(),
  fingerprintImage: FileMetadataSchema.optional(),
  fingerprintString: waferFingerprintSchema.optional(),
  auditState: waferAuditStatusSchema,
  discrepancy: z.string().optional()
});

export const EfemClientConfigSchema = z.object({
  clientId: integerIdSchema,
  name: z.string(),
  isActive: z.boolean(),
});

export const TenantEfemConfigSchema = z.object({
  tenantId: uuidSchema,
  maxClients: z.number().int().min(0),
  clients: z.array(EfemClientConfigSchema)
});

export const TenantEfemConfigResponseSchema = BaseResponseSchema.extend({
  data: TenantEfemConfigSchema
})






export const WaferAuditResponseSchema = z.object({
  results: z.array(WaferAuditResultSchema),
  summary: z.object({
    total: z.number().int(),
    ok: z.number().int(),
    ghostWafers: z.number().int(),
    missingWafers: z.number().int(),
    notAudited: z.number().int()
  })
});

// ========================================
// EFEM Scan with Pre-signed URLs (New Architecture)
// ========================================

/**
 * Request to initiate EFEM scan with pre-signed URL workflow
 */
export const EfemScanWafersRequestSchema = z.object({
  jobId: z.string()
    .regex(/^job-\d+-[a-z0-9]+$/, 'Invalid jobId format. Expected: job-{timestamp}-{uuid}')
    .optional(), // Optional - backend will generate if not provided
  loadPortId: z.number().int().min(1),
  slotNumbers: z.array(containerSlotSchema),
  options: z.object({
    autoOcr: z.boolean().default(true),
    ocrContext: z.nativeEnum(dt.OCR_CONTEXT_TYPE).default(dt.OCR_CONTEXT_TYPE.EFEM_SCAN),
    captureMetadata: z.object({
      operator: z.string().optional(),
      purpose: z.string().optional(),
    }).optional(),
  }).optional(),
});



/**
 * Upload instruction for EFEM client
 */
export const UploadInstructionSchema = z.object({
  slotNumber: containerSlotSchema,
  imageIndex: z.number().int().min(0), // Index of image (0, 1, 2...) for multi-image per wafer
  objectKey: z.string(),
  uploadUrl: z.string().url(),
  expiresAt: z.string().datetime(),
});



/**
 * Single slot scan result with multi-image support
 */
export const SlotScanResultSchema = z.object({
  slotNumber: containerSlotSchema,
  status: slotScanStatusSchema,
  files: z.array(FileMetadataSchema.extend({
    imageIndex: z.number().int().min(0),
    ocr: z.object({
      text: z.string(),
      confidence: z.number().min(0).max(1),
      blurScore: z.number().min(0).max(100),
    }).optional(),
  })).optional(),
  resolvedFingerprint: z.object({
    value: z.string(),
    confidence: z.number().min(0).max(1),
    resolutionMethod: z.nativeEnum(OCR_RESOLUTION_METHOD),
    requiredReview: z.boolean(),
    explanation: z.string(),
  }).optional(),
  error: z.string().optional(),
});



/**
 * Complete scan results response
 */
export const EfemScanResultsSchema = z.object({
  jobId: z.string(),
  status: batchScanStatusSchema,
  results: z.array(SlotScanResultSchema),
  summary: z.object({
    total: z.number().int(),
    successful: z.number().int(),
    withOcr: z.number().int(),
    noWafer: z.number().int(),
    failed: z.number().int(),
    aborted: z.number().int().optional(),
    averageConfidence: z.number().min(0).max(1).optional(),
    averageBlurScore: z.number().min(0).max(100).optional(),
  }),
  timing: z.object({
    scanDuration: z.number().min(0),
    uploadDuration: z.number().min(0),
    ocrDuration: z.number().min(0),
    totalDuration: z.number().min(0),
  }),
});



export const EfemScanWafersResponseSchema = BaseResponseSchema.extend({
  data: EfemScanResultsSchema,
});



/**
 * EFEM -> Backend callback payload
 */
export const EfemScanCallbackSchema = z.object({
  jobId: z.string(),
  clientId: z.string(),
  status: batchScanStatusSchema,
  results: z.array(z.object({
    slotNumber: containerSlotSchema,
    status: slotScanStatusSchema,
    alignmentAngle: z.number().optional(), // Rotation angle if prealigner was used
    images: z.array(z.object({
      imageIndex: z.number().int().min(0),
      objectKey: z.string().optional(),
      size: z.number().int().min(0).optional(),
      status: z.string().optional(), // For partial failures
      error: z.string().optional(),
    })).optional(),
    timestamp: z.string().datetime(),
    error: z.string().optional(),
  })),
  summary: z.object({
    total: z.number().int(),
    successful: z.number().int(),
    noWafer: z.number().int(),
    failed: z.number().int(),
  }),
  errors: z.array(z.object({
    code: z.string(),
    message: z.string(),
  })).optional(),
  completedAt: z.string().datetime().optional(),
});



// ========================================
// Batch Scan Orchestrator Schemas
// ========================================



/**
 * Frontend adapter request for POST /tenant/container-slot-scans (mapped in ApiService).
 */
export const InitiateBatchScanRequestSchema = z.object({
  gatewaySerialNumber: gatewaySerialNumberParamSchema,
  loadPortId: z.number().int().min(1),
  slotNumbers: z.array(containerSlotSchema),
  waferImagingTemplateId: z.number().int().positive(),
  lightingSource: z.enum(['template_starting', 'wafer_learned']).default('template_starting'),
  /** Optional physical wafer id for wafer_learned lighting resolution */
  waferId: integerIdSchema.optional(),
  /** When set, skips load-port binding check and uses this container */
  containerId: integerIdSchema.optional(),
  options: containerSlotScanJobOptionsSchema.optional(),
});



/**
 * Frontend adapter shape for GET /tenant/container-slot-scans/:jobId progress.
 */
export const BatchScanProgressSchema = z.object({
  jobId: z.string(),
  phase: containerSlotScanJobPhaseSchema,
  status: batchScanStatusSchema,
  totalSlots: z.number().int(),
  completedSlots: z.number().int(),
  results: z.array(z.object({
    slotNumber: containerSlotSchema,
    status: slotScanStatusSchema,
    /** Edge slot phase for live monitor UI (picking, aligning, …). */
    livePhase: containerSlotScanSlotPhaseSchema.optional(),
    /** Dominant OCR phase while cloud processes captures. */
    ocrPhase: ocrPhaseSchema.optional(),
    error: z.string().optional(),
    files: z.array(FileMetadataSchema.extend({
      imageIndex: z.number().int().min(0),
      ocr: z.object({
        text: z.string(),
        confidence: z.number().min(0).max(1),
        blurScore: z.number().min(0).max(100),
      }).optional(),
    })).optional(),
    ocrResults: z.array(z.object({
      text: z.string(),
      confidence: z.number().min(0).max(1),
      blurScore: z.number().min(0).max(100),
      imageIndex: z.number().int(),
    })).optional(),
    resolvedFingerprint: z.object({
      value: z.string(),
      confidence: z.number().min(0).max(1),
      resolutionMethod: z.nativeEnum(OCR_RESOLUTION_METHOD),
      requiredReview: z.boolean(),
      explanation: z.string(),
    }).optional(),
    fingerprint: z.string().optional(),
    ocrConfidence: z.number().min(0).max(1).optional(),
    retryCount: z.number().int().min(0).optional(),
  })),
  summary: z.object({
    successful: z.number().int(),
    withOcr: z.number().int(),
    noWafer: z.number().int(),
    failed: z.number().int(),
    ocrFailed: z.number().int(),
  }),
  timing: z.object({
    scanDuration: z.number().min(0),
    uploadDuration: z.number().min(0),
    ocrDuration: z.number().min(0),
    totalDuration: z.number().min(0),
  }),
});



/**
 * Retry OCR request schema
 */
export const RetryOcrRequestSchema = z.object({
  slotNumbers: z.array(containerSlotSchema).min(1),
});



// ========================================
// End container slot scan (frontend BatchScan*) adapter schemas
// ========================================
// Inferred types
export type BatchScanProgress = z.infer<typeof BatchScanProgressSchema>;
export type CreateEfemClient = z.infer<typeof CreateEfemClientSchema>;
export type DeviceShadowStatus = z.infer<typeof DeviceShadowStatusSchema>;
export type EfemClientAuthenticationResult = z.infer<typeof EfemClientAuthenticationResultSchema>;
export type EfemClientAuthenticationResultResponse = z.infer<typeof EfemClientAuthenticationResultResponseSchema>;
export type EfemClientAuthenticationResultResponseData = z.infer<typeof EfemClientAuthenticationResultResponseSchema.shape.data>;
export type EfemClientConfig = z.infer<typeof EfemClientConfigSchema>;
export type EfemClientData = z.infer<typeof EfemClientDataSchema>;
export type EfemClientStatus = z.infer<typeof EfemClientStatusSchema>;
export type EfemClientStatusData = z.infer<typeof EfemClientStatusSchema.shape.data>;
export type EfemConfig = z.infer<typeof EfemConfigSchema>;
export type EfemScanCallback = z.infer<typeof EfemScanCallbackSchema>;
export type EfemScanResults = z.infer<typeof EfemScanResultsSchema>;
export type EfemScanWafersRequest = z.infer<typeof EfemScanWafersRequestSchema>;
export type EfemScanWafersResponse = z.infer<typeof EfemScanWafersResponseSchema>;
export type EfemServiceStatus = z.infer<typeof EfemServiceStatusSchema>;
export type EfemStatusResponse = z.infer<typeof EfemStatusResponseSchema>;
export type EfemStatusResponseData = z.infer<typeof EfemStatusResponseSchema.shape.data>;
export type GetEfemClientsResponse = z.infer<typeof GetEfemClientsResponseSchema>;
export type GetEfemClientsResponseData = z.infer<typeof GetEfemClientsResponseSchema.shape.data>;
export type InitiateBatchScanRequest = z.infer<typeof InitiateBatchScanRequestSchema>;
export type IoTCommandRequest = z.infer<typeof IoTCommandRequestSchema>;
export type IoTCommandResponse = z.infer<typeof IoTCommandResponseSchema>;
export type IoTRuleControlResponseLoose = z.infer<typeof IoTRuleControlResponseLooseSchema>;
export type IotRpcResponseEnvelope = z.infer<typeof IotRpcResponseEnvelopeSchema>;
export type LoadPort = z.infer<typeof LoadPortSchema>;
export type LoadPortsResponse = z.infer<typeof LoadPortsResponseSchema>;
export type LoadPortsResponseData = z.infer<typeof LoadPortsResponseSchema.shape.data>;
export type ProvisionDeviceRequest = z.infer<typeof ProvisionDeviceRequestSchema>;
export type ProvisionDeviceResponse = z.infer<typeof ProvisionDeviceResponseSchema>;
export type RetryOcrRequest = z.infer<typeof RetryOcrRequestSchema>;
export type SlotInfo = z.infer<typeof SlotInfoSchema>;
export type SlotMapData = z.infer<typeof SlotMapResponseSchema.shape.data>;
export type SlotMapResponse = z.infer<typeof SlotMapResponseSchema>;
export type SlotMapResponseData = z.infer<typeof SlotMapResponseSchema.shape.data>;
export type SlotScanResult = z.infer<typeof SlotScanResultSchema>;
export type TenantEfemConfig = z.infer<typeof TenantEfemConfigSchema>;
export type TenantEfemConfigResponse = z.infer<typeof TenantEfemConfigResponseSchema>;
export type TenantEfemConfigResponseData = z.infer<typeof TenantEfemConfigResponseSchema.shape.data>;
export type UploadInstruction = z.infer<typeof UploadInstructionSchema>;
export type WaferAuditResponse = z.infer<typeof WaferAuditResponseSchema>;
export type WaferAuditResult = z.infer<typeof WaferAuditResultSchema>;
export type WaferFingerprintCollectionRequest = z.infer<typeof WaferFingerprintCollectionRequestSchema>;
export type WaferFingerprintResult = z.infer<typeof WaferFingerprintResultSchema>;
export type WaferImageCollectionResponse = z.infer<typeof WaferImageCollectionResponseSchema>;
export type WaferImageCollectionResponseData = z.infer<typeof WaferImageCollectionResponseSchema.shape.data>;
